import React, { useEffect } from 'react'
import dynamic from 'next/dynamic'

import { TypeAssetLottieInteractiveAnimation } from '@lib/types'
import { usePrevious } from '@lib/hooks'
import { useForceRerender } from '@lib/hooks/force-rerender'
import { type InteractiveLottiePlayerEvents } from 'src/components/asset/lottie'
import { updateAssetCDNUrl } from '@components/asset/utils'
import { ContentRenderer } from '@components/renderer'
import { useInteractiveLottieGnarTracking } from '@components/asset/lottie/hooks'

const DynamicInteractiveLottiePlayer = dynamic(
  () => import('@components/asset/lottie/players/interactive-lottie-player').then((m) => m.InteractiveLottiePlayer),
  { ssr: false }
)

export interface LottiePlayerWrapperProps {
  structure: TypeAssetLottieInteractiveAnimation
  index: number
  activeTabIndex: number
  activeTabLabel: string
  events?: InteractiveLottiePlayerEvents
}

export const UseCaseLottiePlayer: React.FC<LottiePlayerWrapperProps> = ({
  structure,
  index,
  activeTabIndex,
  activeTabLabel,
}) => {
  const [key, forceRerender] = useForceRerender()
  const prevActiveTabIndex = usePrevious(activeTabIndex)
  const gnarTracking = useInteractiveLottieGnarTracking()

  useEffect(() => {
    // Rerender previous tab, in order to restart animation/video when active tab changed
    if (prevActiveTabIndex === index && index !== activeTabIndex) {
      forceRerender()
    }
  }, [activeTabIndex, index, forceRerender, prevActiveTabIndex])

  const { jsonUrl, description, placeholderImage, fallbackVideo, ctAs } = structure.fields

  return (
    <DynamicInteractiveLottiePlayer
      key={key}
      jsonUrl={jsonUrl}
      description={description}
      fallbackImage={
        placeholderImage?.fields?.file && {
          url: updateAssetCDNUrl(placeholderImage?.fields?.file?.url),
          alt: placeholderImage?.fields?.description,
        }
      }
      fallbackVideo={fallbackVideo.fields.file}
      cta={<ContentRenderer structure={ctAs} />}
      events={{
        ...gnarTracking,
        onAnimationLoaded: () => {
          return gnarTracking.onAnimationLoaded(`Tab – ${activeTabLabel}`)
        },
        onAnimationClicked: (segmentIndex) => {
          return gnarTracking.onAnimationClicked(segmentIndex, `Tab – ${activeTabLabel}`)
        },
      }}
    />
  )
}
